import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Modal,
  message,
  Spin,
  Empty,
  Dropdown,
  MenuProps,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  AuditOutlined,
  StarOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "antd/es/table";
import { Planogram, PlanogramStatus } from "../../../types/storeGroup";
import StoreGroupService from "../../../services/storeGroupService";
import ShelfPreviewDialog from "../../../components/common/ShelfEditor";
import dayjs from "dayjs";

interface PlanogramListProps {
  storeGroupCode: string;
  storeId?: string;
  readOnly?: boolean;
}

const STATUS_COLORS: Record<PlanogramStatus, string> = {
  DRAFT: "orange",
  IN_REVIEW: "blue",
  APPROVED: "green",
  REJECTED: "red",
  ACTIVE: "green",
  ARCHIVED: "red",
};

// Status translation mapping
const getStatusTranslation = (status: PlanogramStatus, t: any): string => {
  const statusMap: Record<PlanogramStatus, string> = {
    DRAFT: t("storeGroup.benchmarkManagement.planogram.status.draft"),
    IN_REVIEW: t(
      "storeGroup.benchmarkManagement.planogram.status.pendingReview"
    ),
    APPROVED: t("storeGroup.benchmarkManagement.planogram.status.approved"),
    REJECTED: t("storeGroup.benchmarkManagement.planogram.status.rejected"),
    ACTIVE: t("storeGroup.benchmarkManagement.planogram.status.active"),
    ARCHIVED: t("storeGroup.benchmarkManagement.planogram.status.archived"),
  };
  return statusMap[status] || status;
};

// Main Version translation
const getMainVersionTranslation = (isMainVersion: boolean, t: any): string => {
  return isMainVersion ? t("common.yes") : t("common.no");
};

const PlanogramList: React.FC<PlanogramListProps> = ({
  storeGroupCode,
  storeId,
  readOnly = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [planograms, setPlanograms] = useState<Planogram[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [shelfPreviewDialogVisible, setShelfPreviewDialogVisible] =
    useState<boolean>(false);
  const [shelfPreviewDialogIsPreview, setShelfPreviewDialogIsPreview] =
    useState<boolean>(true);
  const [shelfPreviewDialogData, setShelfPreviewDialogData] =
    useState<Planogram | null>(null);

  const storeGroupService = StoreGroupService;

  useEffect(() => {
    fetchPlanograms();
  }, [storeGroupCode, storeId]);

  const fetchPlanograms = async () => {
    setLoading(true);
    try {
      // 使用现有的 getPlanograms 方法
      const response = await storeGroupService.getPlanograms(storeGroupCode);
      if (response.success && response.data) {
        // 按导入时间降序排序，确保最新导入的数据显示在最上面
        const sortedPlanograms = response.data;
        setPlanograms(sortedPlanograms);
      } else {
        message.error(t("error.fetchPlanogramListFailed"));
      }
    } catch (error) {
      message.error(t("error.fetchPlanogramListFailed"));
      console.error("Failed to fetch planogram list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (code: string) => {
    if (storeId) {
      navigate(
        `/store-group/${storeGroupCode}/store/${storeId}/planogram/${code}/edit`
      );
    } else {
      navigate(`/store-group/${storeGroupCode}/planogram/${code}/edit`);
    }
  };

  const handleView = (code: string) => {
    if (storeId) {
      navigate(
        `/store-group/${storeGroupCode}/store/${storeId}/planogram/${code}`
      );
    } else {
      navigate(`/store-group/${storeGroupCode}/planogram/${code}`);
    }
  };

  const handleSubmitReview = (record: Planogram) => {
    Modal.confirm({
      title: t("storeGroup.benchmarkManagement.planogram.actions.submitReview"),
      content: t("storeGroup.planogram.messages.submitConfirm", {
        planogramId: record.planogramId,
      }),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: async () => {
        try {
          const response = await storeGroupService.submitPlanogramForReview(
            record.id
          );
          if (response.success) {
            message.success(t("storeGroup.planogram.messages.submitSuccess"));
            fetchPlanograms();
          } else {
            message.error(t("storeGroup.planogram.messages.submitFailed"));
          }
        } catch (error) {
          message.error(t("storeGroup.planogram.messages.submitFailed"));
          console.error("Submit review failed:", error);
        }
      },
    });
  };

  const handleSetMainVersion = (record: Planogram) => {
    if (record.isMainVersion) {
      message.info(t("storeGroup.planogram.messages.alreadyMainVersion"));
      return;
    }

    Modal.confirm({
      title: t(
        "storeGroup.benchmarkManagement.planogram.actions.setMainVersion"
      ),
      content: (
        <div>
          <p>
            {t("storeGroup.planogram.messages.setMainVersionConfirm", {
              planogramId: record.planogramId,
            })}
          </p>
          <div
            style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: "#f6f8fa",
              borderRadius: 6,
            }}
          >
            <strong>
              {t("storeGroup.planogram.messages.setMainVersionNotice.title")}
            </strong>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>
                {t(
                  "storeGroup.planogram.messages.setMainVersionNotice.baseline"
                )}
              </li>
              <li>
                {t(
                  "storeGroup.planogram.messages.setMainVersionNotice.cancelOthers"
                )}
              </li>
              <li>
                {t("storeGroup.planogram.messages.setMainVersionNotice.impact")}
              </li>
            </ul>
          </div>
        </div>
      ),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      okType: "primary",
      width: 520,
      onOk: async () => {
        try {
          const response = await storeGroupService.setAsMainVersion(record.id);
          if (response.success) {
            message.success(
              t("storeGroup.planogram.messages.setMainVersionSuccess")
            );
            fetchPlanograms();
          } else {
            message.error(
              t("storeGroup.planogram.messages.setMainVersionFailed")
            );
          }
        } catch (error) {
          message.error(
            t("storeGroup.planogram.messages.setMainVersionFailed")
          );
          console.error("Set main version failed:", error);
        }
      },
    });
  };

  const handleDownload = async (record: Planogram) => {
    try {
      const result = await storeGroupService.exportPlanogramBenchmark(
        record.id
      );
      message.success(
        t("storeGroup.planogram.messages.downloadStarted", {
          planogramId: record.planogramId,
        })
      );

      // 模拟下载文件
      const link = document.createElement("a");
      if (result.data) {
        link.href = result.data.downloadUrl;
        link.download = result.data.fileName;
      } else {
        message.error(t("storeGroup.exportDataFormatError"));
        return;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      message.error(t("storeGroup.planogram.messages.downloadFailed"));
      console.error("Download failed:", error);
    }
  };

  const handlePreview = (record: Planogram, isPreview: boolean) => {
    try {
      // TODO: 实现在线预览功能
      setShelfPreviewDialogVisible(true);
      setShelfPreviewDialogData(record);
      setShelfPreviewDialogIsPreview(!!isPreview);
      // 实际应该调用预览 API 或打开预览组件
    } catch (error) {
      message.error(t("storeGroup.planogram.messages.previewFailed"));
      console.error("Preview failed:", error);
    }
  };

  const columns: ColumnsType<Planogram> = [
    {
      title: "Planogram ID",
      dataIndex: "planogramId",
      key: "planogramId",
      width: 150,
      render: (planogramId: string) => (
        <span style={{ fontFamily: "monospace" }}>{planogramId}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: PlanogramStatus) => (
        <Tag color={STATUS_COLORS[status]}>
          {getStatusTranslation(status, t)}
        </Tag>
      ),
    },
    {
      title: "Main Version",
      dataIndex: "isMainVersion",
      key: "isMainVersion",
      width: 120,
      render: (isMainVersion: boolean) => (
        <Tag color={isMainVersion ? "green" : "default"}>
          {getMainVersionTranslation(isMainVersion, t)}
        </Tag>
      ),
    },
    {
      title: t("storeGroup.benchmarkManagement.planogram.importTime"),
      dataIndex: "gmtCreate",
      key: "gmtCreate",
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t("common.creator"),
      dataIndex: "creatorName",
      key: "creatorName",
      width: 180
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.planogramApproverStatus"
      ),
      dataIndex: "planogramApproverStatus",
      key: "planogramApproverStatus",
      width: 180,
      render: (status: PlanogramStatus) => (
        <Tag color={STATUS_COLORS[status]}>
          {getStatusTranslation(status, t)}
        </Tag>
      ),
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.planogramApproverName"
      ),
      dataIndex: "planogramApproverName",
      key: "planogramApproverName",
      width: 180,
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.planogramApproverTime"
      ),
      dataIndex: "planogramApproverTime",
      key: "planogramApproverTime",
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.commercialApproverStatus"
      ),
      dataIndex: "commercialApproverStatus",
      key: "commercialApproverStatus",
      width: 180,
      render: (status: PlanogramStatus) => (
        <Tag color={STATUS_COLORS[status]}>
          {getStatusTranslation(status, t)}
        </Tag>
      ),
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.commercialApproverName"
      ),
      dataIndex: "commercialApproverName",
      key: "commercialApproverName",
      width: 180,
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.commercialApproverTime"
      ),
      dataIndex: "commercialApproverTime",
      key: "commercialApproverTime",
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.operationApproverStatus"
      ),
      dataIndex: "operateApproverStatus",
      key: "operateApproverStatus",
      width: 180,
      render: (status: PlanogramStatus) => (
        <Tag color={STATUS_COLORS[status]}>
          {getStatusTranslation(status, t)}
        </Tag>
      ),
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.operationApproverName"
      ),
      dataIndex: "operateApproverName",
      key: "operateApproverName",
      width: 180,
    },
    {
      title: t(
        "storeGroup.benchmarkManagement.planogram.operationApproverTime"
      ),
      dataIndex: "operateApproverTime",
      key: "operationApproverTime",
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t("common.updateTime"),
      dataIndex: "gmtModified",
      key: "gmtModified",
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t("common.modifier"),
      dataIndex: "modifierName",
      key: "modifierName",
      width: 180,
    },
    {
      title: t("common.actions"),
      key: "action",
      width: 380,
      fixed: "right" as const,
      render: (_, record) => {
        // 定义所有可能的操作按钮
        const allActions = [
          {
            key: "onlineEdit",
            label: t("storeGroup.benchmarkManagement.planogram.actions.onlineEdit"),
            icon: <EditOutlined />,
            disabled: record.status !== "DRAFT",
            onClick: () => {
              handlePreview(record, false);
            }
          },
          {
            key: "submitReview",
            label: t("storeGroup.benchmarkManagement.planogram.actions.submitReview"),
            icon: <AuditOutlined />,
            disabled: record.status !== "DRAFT",
            onClick: () => handleSubmitReview(record)
          },
          {
            key: "setMainVersion",
            label: t("storeGroup.benchmarkManagement.planogram.actions.setMainVersion"),
            icon: <StarOutlined />,
            disabled: record.isMainVersion || record.status !== "APPROVED",
            onClick: () => handleSetMainVersion(record)
          },
          {
            key: "onlinePreview",
            label: t("storeGroup.benchmarkManagement.planogram.actions.onlinePreview"),
            icon: <EyeOutlined />,
            disabled: readOnly,
            onClick: () => handlePreview(record, true)
          },
          {
            key: "downloadData",
            label: t("storeGroup.benchmarkManagement.planogram.actions.downloadData"),
            icon: <DownloadOutlined />,
            disabled: readOnly,
            onClick: () => handleDownload(record)
          }
        ];

        // 过滤出可用的操作（不禁用的按钮）
        const availableActions = allActions.filter(action => !action.disabled);

        // 只显示前3个操作按钮
        const visibleActions = availableActions.slice(0, 3);
        const hiddenActions = availableActions.slice(3);

        return (
          <Space size="small">
            {visibleActions.map(action => (
              <Tooltip key={action.key} title={action.label}>
                <Button
                  type="text"
                  size="small"
                  icon={action.icon}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              </Tooltip>
            ))}

            {hiddenActions.length > 0 && (
              <Dropdown
                menu={{
                  items: hiddenActions.map(action => ({
                    key: action.key,
                    icon: action.icon,
                    label: action.label,
                    onClick: action.onClick
                  }))
                }}
                trigger={['click']}
              >
                <Button type="text" size="small" icon={<MoreOutlined />}>
                  {t("common.more")}
                </Button>
              </Dropdown>
            )}
          </Space>
        );
      },
    },
  ];

  const formatDateTime = (timestamp: number): string => {
    if (!timestamp) {
      return "-";
    }
    return dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");
  };

  return (
    <div className="planogram-list">
      <Table
        columns={columns}
        dataSource={planograms}
        rowKey="code"
        loading={loading}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: loading ? (
            <Spin />
          ) : (
            <Empty description={t("planogram.noPlanograms")} />
          ),
        }}
        scroll={{ x: 1200, y: 500 }}
      />

      <ShelfPreviewDialog
        visible={shelfPreviewDialogVisible}
        setVisible={setShelfPreviewDialogVisible}
        data={shelfPreviewDialogData}
        origin={"planList"}
        canEdit={!shelfPreviewDialogIsPreview}
      />
    </div>
  );
};

export default PlanogramList;
